// State machine for one conversation. Holds the message list, dispatches
// socket events into structured state, exposes a sendQuery function that
// auto-routes between ai:chart_query (first turn) and ai:user_message
// (follow-ups).

import { useEffect, useReducer, useCallback } from "react";
import { CLIENT_EVENTS, SERVER_EVENTS } from "../types/events";

// Humanize a structured ai:progress event ({ event, payload }) from the ai-assistant loops'
// shareProgress callback into a one-line narration (B-UX-STREAM-2 verbose default with real
// counts). Unknown event names fall back to a prettified label so new backend events still
// surface without a frontend change. Returns null for events we deliberately don't narrate.
function humanizeProgress(event, payload) {
  const p = payload || {};
  switch (event) {
    case "fetching_execution":     return p.executionRef ? `Looking up ${p.executionRef}…` : "Looking up record…";
    case "execution_not_found":    return null; // the response itself reports "not found"
    case "resolving_template":     return "Resolving the template version…";
    case "fetching_answers":       return "Reading answers…";
    case "fetching_answers_done":  return p.answer_count != null ? `Read ${p.answer_count} answers.` : null;
    case "execution_assembled":    return p.sections != null ? `Assembled ${p.sections} sections, ${p.answered}/${p.questions} answered.` : "Record assembled.";
    case "finding_executions":     return p.module ? `Searching ${p.module} records…` : "Searching records…";
    case "finding_question":       return p.intent ? `Finding the "${p.intent}" question…` : "Finding the question…";
    case "aggregating_answers":    return "Aggregating answers…";
    case "finding_spawned_tasks":  return "Finding spawned tasks…";
    case "finding_spawned_executions": return "Finding spawned records…";
    case "fetching_history":       return "Reading the history trail…";
    case "web_search_started":     return "Searching the web…";
    case "web_fetching":           return p.host ? `Fetching ${p.host}…` : "Fetching a web page…";
    case "assumption_stated":      return null; // surfaced separately as an assumption note
    default:
      // Generic mode (payload omitted) or an unrecognized event → prettify the name.
      return String(event || "").replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()) + "…";
  }
}

const initial = (id, tenantSchema) => ({
  id,
  tenantSchema,
  messages: [],
  status: "idle",
  lastError: null,
});

function reducer(state, action) {
  switch (action.type) {
    case "reset":
      return initial(action.id, action.tenantSchema);

    case "add_user_message":
      return {
        ...state,
        status: "in_flight",
        lastError: null,
        messages: [
          ...state.messages,
          { id: action.id, role: "user", text: action.text, timestamp: new Date().toISOString() },
        ],
      };

    case "start_assistant_message":
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            id: action.id, role: "assistant",
            thinking: "", visibleText: "",
            toolCalls: [],
            timestamp: new Date().toISOString(),
          },
        ],
      };

    case "append_thinking": {
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last && last.role === "assistant") {
        messages[messages.length - 1] = {
          ...last,
          thinking: (last.thinking || "") + action.text,
          currentStage: null,
          lastEventType: "thinking",
        };
      }
      return { ...state, messages };
    }

    case "append_text": {
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last && last.role === "assistant") {
        messages[messages.length - 1] = {
          ...last,
          visibleText: (last.visibleText || "") + action.text,
          currentStage: null,
          lastEventType: "text",
        };
      }
      return { ...state, messages };
    }

    case "set_stage": {
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last && last.role === "assistant") {
        messages[messages.length - 1] = { ...last, currentStage: action.stage };
      }
      return { ...state, messages };
    }

    case "tool_call_event": {
      // share_progress doesn't render as a pill — its narration is surfaced
      // via the progressItems array (driven by the ai:progress_text event).
      // state_assumption likewise isn't a pill — it surfaces as the prominent
      // AssumptionBanner (driven by the ai:assumption event, Sprint 8a). Drop
      // both at the reducer level so the toolCalls list stays focused on tools
      // the user benefits from seeing as pills.
      if (action.toolCall && (action.toolCall.name === "share_progress" || action.toolCall.name === "state_assumption")) {
        return state;
      }
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last && last.role === "assistant") {
        const tc = last.toolCalls || [];
        const existing = tc.findIndex((c) => c.tool_use_id === action.toolCall.tool_use_id);
        const updated = existing >= 0
          ? tc.map((c, i) => (i === existing ? { ...c, ...action.toolCall } : c))
          : [...tc, action.toolCall];
        messages[messages.length - 1] = {
          ...last,
          toolCalls: updated,
          currentStage: null,
          lastEventType: "tool",
        };
      }
      return { ...state, messages };
    }

    case "append_progress": {
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last && last.role === "assistant") {
        const progressItems = last.progressItems || [];
        const lastItem = progressItems[progressItems.length - 1];
        let nextItems;
        if (lastItem && lastItem.text === action.text) {
          // Bug 6: many parallel tool calls fire the same narration (e.g. "Aggregating
          // answers…" ×11). Collapse consecutive identical lines into one with a counter
          // instead of stacking duplicates.
          nextItems = progressItems.map((it, i) =>
            i === progressItems.length - 1 ? { ...it, count: (it.count || 1) + 1 } : it
          );
        } else {
          nextItems = [...progressItems, {
            id: `p-${Date.now()}-${progressItems.length}`,
            text: action.text,
            count: 1,
            timestamp: new Date().toISOString(),
          }];
        }
        messages[messages.length - 1] = {
          ...last,
          progressItems: nextItems,
          currentStage: null,
          lastEventType: "progress",
        };
      }
      return { ...state, messages };
    }

    case "add_assumption": {
      // state_assumption fired (B-AMB-1). Attach to the current assistant message so the
      // AssumptionBanner can render it prominently at the top. A turn may state more than one
      // assumption, so we append.
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last && last.role === "assistant") {
        const assumptions = last.assumptions || [];
        messages[messages.length - 1] = {
          ...last,
          assumptions: [...assumptions, { text: action.text, rationale: action.rationale || null }],
        };
      }
      return { ...state, messages };
    }

    case "add_chart": {
      // Charts append rather than overwrite — a single assistant turn
      // can produce multiple charts (e.g. "show A and B as two charts"),
      // and we render them stacked in arrival order.
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last && last.role === "assistant") {
        const charts = last.charts || [];
        messages[messages.length - 1] = {
          ...last,
          charts: [...charts, action.spec],
          lastEventType: "chart",
        };
      }
      return { ...state, messages };
    }

    case "set_clarification": {
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last && last.role === "assistant") {
        messages[messages.length - 1] = {
          ...last,
          clarification: action.payload,
          lastEventType: "clarification",
        };
      }
      return { ...state, messages, status: "awaiting_clarification" };
    }

    case "mark_clarification_answered": {
      const messages = state.messages.map((msg, i) => {
        if (i !== action.index) return msg;
        return {
          ...msg,
          clarification: { ...msg.clarification, selectedOption: action.option, answered: true },
        };
      });
      return { ...state, messages };
    }

    case "mark_done": {
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last && last.role === "assistant") {
        messages[messages.length - 1] = {
          ...last,
          tokens: action.tokens, durationMs: action.durationMs,
          currentStage: null,
          lastEventType: "done",
        };
      }
      const newStatus = state.status === "awaiting_clarification" ? "awaiting_clarification" : "idle";
      return { ...state, messages, status: newStatus };
    }

    case "set_error":
      return { ...state, status: "error", lastError: action.error };

    default:
      return state;
  }
}

export function useConversation({ socket, conversationId, tenantSchema, provider }) {
  const [state, dispatch] = useReducer(reducer, conversationId, (id) => initial(id, tenantSchema));

  // Reset state when the conversation switches.
  useEffect(() => {
    dispatch({ type: "reset", id: conversationId, tenantSchema });
  }, [conversationId, tenantSchema]);

  // Subscribe socket events into the reducer.
  useEffect(() => {
    if (!socket) return;
    const matches = (payload) => payload && payload.conversationId === conversationId;

    const handlers = {
      [SERVER_EVENTS.THINKING_DELTA]: (p) => matches(p) && dispatch({ type: "append_thinking", text: p.text }),
      [SERVER_EVENTS.TEXT_DELTA]:     (p) => matches(p) && dispatch({ type: "append_text", text: p.text }),
      [SERVER_EVENTS.TOOL_CALL]:      (p) => matches(p) && dispatch({
        type: "tool_call_event",
        // Forward the full payload (minus conversationId, used only for
        // routing) so fields like executedSql / rowCount land on the
        // toolCall entry without being dropped at this hop.
        toolCall: {
          tool_use_id: p.tool_use_id,
          name: p.name,
          status: p.status,
          summary: p.summary,
          error: p.error,
          executedSql: p.executedSql,
          rowCount: p.rowCount,
        },
      }),
      [SERVER_EVENTS.CHART]:          (p) => matches(p) && dispatch({ type: "add_chart", spec: p.spec }),
      [SERVER_EVENTS.CLARIFICATION]:  (p) => matches(p) && dispatch({
        type: "set_clarification",
        payload: { question: p.question, options: p.options, context: p.context },
      }),
      [SERVER_EVENTS.PROGRESS_TEXT]:  (p) => matches(p) && dispatch({ type: "append_progress", text: p.text }),
      [SERVER_EVENTS.PROGRESS]:       (p) => {
        if (!matches(p)) return;
        const text = humanizeProgress(p.event, p.payload);
        if (text) dispatch({ type: "append_progress", text });
      },
      [SERVER_EVENTS.ASSUMPTION]:     (p) => matches(p) && dispatch({ type: "add_assumption", text: p.assumption, rationale: p.rationale }),
      [SERVER_EVENTS.STAGE]:          (p) => matches(p) && dispatch({ type: "set_stage", stage: p.stage }),
      [SERVER_EVENTS.DONE]:           (p) => matches(p) && dispatch({ type: "mark_done", tokens: p.tokens, durationMs: p.durationMs }),
      [SERVER_EVENTS.ERROR]:          (p) => matches(p) && dispatch({ type: "set_error", error: { code: p.code, message: p.message } }),
    };

    for (const [event, handler] of Object.entries(handlers)) socket.on(event, handler);
    return () => {
      for (const [event, handler] of Object.entries(handlers)) socket.off(event, handler);
    };
  }, [socket, conversationId]);

  const sendQuery = useCallback((text) => {
    if (!socket) return;
    const userMsgId = `u-${Date.now()}`;
    const assistantMsgId = `a-${Date.now()}`;
    dispatch({ type: "add_user_message", id: userMsgId, text });
    dispatch({ type: "start_assistant_message", id: assistantMsgId });

    const isFirstMessage = state.messages.length === 0;
    if (isFirstMessage) {
      socket.emit(CLIENT_EVENTS.CHART_QUERY, {
        conversationId, queryText: text, tenantSchema, provider,
      });
    } else {
      socket.emit(CLIENT_EVENTS.USER_MESSAGE, { conversationId, text, tenantSchema, provider });
    }
  }, [socket, conversationId, tenantSchema, provider, state.messages.length]);

  const cancel = useCallback(() => {
    if (!socket) return;
    socket.emit(CLIENT_EVENTS.CANCEL, { conversationId });
  }, [socket, conversationId]);

  const selectClarificationOption = useCallback((option) => {
    if (state.status !== "awaiting_clarification") return;
    let lastClarIdx = -1;
    for (let i = state.messages.length - 1; i >= 0; i--) {
      if (state.messages[i].role === "assistant" && state.messages[i].clarification) {
        lastClarIdx = i;
        break;
      }
    }
    if (lastClarIdx === -1) return;

    dispatch({ type: "mark_clarification_answered", index: lastClarIdx, option });
    // sendQuery routes as ai:user_message because messages.length > 0.
    sendQuery(option);
  }, [state.status, state.messages, sendQuery]);

  return { state, sendQuery, cancel, selectClarificationOption };
}
